'use client'
import { v4 } from 'uuid'
import { ANALYTICS_EVENT } from '../constants'
import type { AnalyticsEventData } from '../types'

/**
 * Sends a custom analytics event to the client-side event dispatcher.
 *
 * @description
 * This function creates and dispatches a `CustomEvent` containing the provided
 * analytics data. Each event is assigned a unique `eventId` using a UUID.
 *
 * This function only runs in a browser environment. If executed on the server,
 * it exits early without dispatching an event.
 *
 * **Note:** To ensure events are fired when testing internal users, add your email
 * to the `INTERNAL_EXCEPTIONS` constant in the `isInternalUser` folder.
 *
 * @param {AnalyticsEventData} data - The analytics event payload.
 * @returns {void} This function does not return a value.
 */
export function sendAnalyticsEvent(data: AnalyticsEventData) {
  const isServerSide = typeof window === 'undefined'

  if (isServerSide) return

  const event = new CustomEvent(ANALYTICS_EVENT, {
    detail: {
      ...data,
      eventId: v4(),
    },
  })

  window.dispatchEvent(event)
}
